import { useState, FormEvent, ChangeEvent } from 'react';
import { useNotification } from '../shared/NotificationModal';
import MfaVerification from './MfaVerification';
import Button from '../shared/Button';
import { FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../shared/FormInput';
import { TEXT } from '../shared/Typography';

const API_BASE = import.meta.env.VITE_API_BASE || '';

interface UserData {
  token: string;
  userId?: string;
  email?: string;
  role?: string;
  isActive?: boolean;
  roles?: string[];
  fullName?: string;
  _computedRoles?: string[];
}

interface LoginProps {
  onLogin: (_userData: UserData) => void | Promise<void>;
}

interface MfaState {
  mfaToken: string;
  maskedEmail: string;
}

export default function Login({ onLogin }: LoginProps) {
  const notification = useNotification();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [showCredentials, setShowCredentials] = useState<boolean>(false);
  const [mfaState, setMfaState] = useState<MfaState | null>(null);

  const submit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (busy) return;

    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isLocalhost && !password && email) {
      window.location.href = `${API_BASE}/auth/dev-login?email=${encodeURIComponent(email)}`;
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        notification.showError(data?.error || 'Login failed', 'Login Failed');
        setBusy(false);
        return;
      }

      // MFA required — show verification screen
      if (data.mfaRequired) {
        setMfaState({ mfaToken: data.mfaToken, maskedEmail: data.maskedEmail });
        setBusy(false);
        return;
      }

      // Direct login (shouldn't happen for local creds, but handle gracefully)
      const userData = { ...data.user, token: data.token };
      onLogin(userData);
    } catch (_err) {
      notification.showError('An unexpected error occurred. Please try again.', 'Login Error');
    } finally {
      setBusy(false);
    }
  };

  const loginSSO = (): void => {
    window.location.href = `${API_BASE}/auth/saml`;
  };

  const handleMfaVerified = (data: { token: string; user: any }) => {
    const userData = { ...data.user, token: data.token };
    onLogin(userData);
  };

  const handleMfaCancel = () => {
    setMfaState(null);
    setPassword('');
  };

  // ── MFA verification screen ──────────────────────────────────────────────
  if (mfaState) {
    return (
      <MfaVerification
        mfaToken={mfaState.mfaToken}
        maskedEmail={mfaState.maskedEmail}
        onVerified={handleMfaVerified}
        onCancel={handleMfaCancel}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4 sm:p-6 md:p-8">
      <div className="bg-white shadow-md rounded-lg w-full max-w-sm sm:max-w-md md:max-w-lg overflow-hidden">
        {/* SSO Section — Primary action for GRPS employees */}
        <div className="bg-blue-50 px-6 sm:px-10 md:px-12 pt-8 sm:pt-10 pb-6 sm:pb-8">
          <div className="flex flex-col items-center text-center">
            <img
              src="/grautomates-dark.png"
              alt="GRAutomates"
              className="h-20 sm:h-24 w-auto object-contain mb-4"
            />
            <h2 className={`sm:text-lg ${TEXT.cardTitle}`}>GRPS Employees</h2>
            <p className={`mt-1 mb-5 ${TEXT.bodyMuted}`}>Sign in with your company account</p>
            <Button variant="primary" size="lg" onClick={loginSSO} fullWidth>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
              </svg>
              GRPS Single Sign On
            </Button>
          </div>
        </div>

        {/* External Users — Collapsible credential form */}
        <div className="px-6 sm:px-10 md:px-12 pt-5 pb-6 sm:pb-8">
          <button
            type="button"
            onClick={() => setShowCredentials((prev) => !prev)}
            className={`w-full flex items-center justify-center gap-2 hover:text-gray-700 transition-colors py-1 ${TEXT.helper}`}
          >
            <span>External user? Sign in with credentials</span>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${showCredentials ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showCredentials && (
            <form onSubmit={submit} className="space-y-4 sm:space-y-5 mt-5">
              <div>
                <label htmlFor="login-email" className={FIELD_LABEL_CLASS}>Email</label>
                <input
                  id="login-email"
                  type="email"
                  className={FIELD_INPUT_CLASS}
                  value={email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div>
                <label htmlFor="login-password" className={FIELD_LABEL_CLASS}>Password</label>
                <input
                  id="login-password"
                  type="password"
                  className={FIELD_INPUT_CLASS}
                  value={password}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              {(window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1') && (
                <p className={TEXT.caption}>
                  Leave password blank to use dev-login (localhost only)
                </p>
              )}
              <Button variant="primary" size="lg" type="submit" disabled={busy} fullWidth>
                {busy ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-10 md:px-12 py-4 sm:py-5 bg-gray-50 border-t border-gray-100">
          <div className={`flex items-center justify-center gap-3 sm:text-sm ${TEXT.caption}`}>
            <a
              href="/privacy-policy"
              className="hover:text-blue-600 transition-colors"
            >
              Privacy Policy
            </a>
            <span className="text-gray-300">|</span>
            <span>&copy; {new Date().getFullYear()} GR Production Services</span>
          </div>
        </div>
      </div>
    </div>
  );
}
